import { useEffect } from 'react';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { useDiffCheck } from '../../../../common/hooks/use-diff-check/index.tsx';
import type { CardId, Card } from '../../../../model/types.tsx';
import { cardEstimateSet } from '../../../../state/actions/estimate/index.tsx';
import { useBacklogSelector, useBacklogActionCreator } from '../../../../state/index.tsx';
import { getStoryPointFieldId } from '../../../../state/selectors/app/index.tsx';
import { isNumericEstimateTypeSelector } from '../../../../state/selectors/estimation/index.tsx';

type Props = {
	card: Card;
};

const FieldSyncStoryPoint = ({ card: { id, key, estimate } }: Props) => {
	const storyPointFieldId = useBacklogSelector(getStoryPointFieldId);
	const isNumericEstimateType = useBacklogSelector(isNumericEstimateTypeSelector);
	const setStoryPointInBacklog = useBacklogActionCreator(
		(cardId: CardId, storyPoints: number | null) => cardEstimateSet(cardId, { storyPoints }),
	);
	const StoryPointKey = storyPointFieldId || '';
	const storyPointFromBacklog = estimate?.storyPoints;

	// This hook updates the state used by bento in the field value service. It's necessary to provide the initial value
	// and to update the bento store when the story point changes from elsewhere in the backlog
	const [storyPointFromExtractedFields, { setFieldValue: setStoryPointInExtractedFields }] =
		useFieldValue({
			issueKey: key,
			fieldKey: StoryPointKey,
		});

	const storyPointFromBacklogChanged = useDiffCheck([storyPointFromBacklog]);
	const storyPointFromExtractedFieldsChanged = useDiffCheck([storyPointFromExtractedFields]);

	const storesNeedToSync = storyPointFromBacklog !== storyPointFromExtractedFields;

	useEffect(() => {
		const storyPointFromBacklogIsDefined = storyPointFromBacklog !== undefined;

		const shouldSync =
			StoryPointKey &&
			storyPointFromBacklogIsDefined &&
			storyPointFromBacklogChanged &&
			storesNeedToSync &&
			isNumericEstimateType;

		// STORY POINT SYNC: BACKLOG -> EXTRACTED FIELDS
		// Update sweet state (extracted field) when the story point value changes elsewhere or the card is initially rendered
		if (shouldSync) {
			setStoryPointInExtractedFields(key, StoryPointKey, storyPointFromBacklog);
		}
	}, [
		StoryPointKey,
		key,
		storyPointFromBacklog,
		storyPointFromBacklogChanged,
		storesNeedToSync,
		setStoryPointInExtractedFields,
		isNumericEstimateType,
	]);

	useEffect(() => {
		const storyPointFromExtractedFieldsIsDefined = storyPointFromExtractedFields !== undefined;

		const shouldSync =
			!storyPointFromBacklogChanged &&
			storyPointFromExtractedFieldsIsDefined &&
			storyPointFromExtractedFieldsChanged &&
			storesNeedToSync &&
			isNumericEstimateType;

		// STORY POINT SYNC: EXTRACTED FIELDS -> BACKLOG
		// Update the backlog store if the value of the story point is changed within extracted fields
		if (shouldSync) {
			setStoryPointInBacklog(id, storyPointFromExtractedFields);
		}
	}, [
		id,
		storyPointFromBacklogChanged,
		storyPointFromExtractedFields,
		storyPointFromExtractedFieldsChanged,
		storesNeedToSync,
		setStoryPointInBacklog,
		isNumericEstimateType,
	]);

	return null;
};

export default FieldSyncStoryPoint;
